import moment from 'moment-jalaali';

import { endOfDate, startOfDate } from 'utils/date';
import { englishNumber, convertDigitToProperFormat } from 'utils/number';

import messages from './Messages';
import { TimeScale } from 'store/survey/surveyTypes';
import i18n from '../../../../../../../i18n';

moment.locale('fa');
moment.loadPersian({ dialect: 'persian-modern', usePersianDigits: true });

interface IPeriodObjectValues {
  end: number;
  name: string;
  start: number;
  middle: number;
  superName: string;
  prevTitle: string;
  nextTitle: string;
}

interface IPeriodTimeValues {
  [periodName: string]: IPeriodObjectValues;
}

interface IDatePeriod {
  init: () => IPeriodTimeValues;
  values: IPeriodTimeValues;
  periodType: TimeScale;
  currentDate: Date;
  lang: 'fa_IR@calendar=persian' | 'en';
}

abstract class DataPeriod implements IDatePeriod {
  public lang: 'fa_IR@calendar=persian' | 'en';
  constructor() {
    this.values = {};
    this.currentDate = new Date();
    this.periodType = 'month';
    this.lang = 'fa_IR@calendar=persian';
  }
  values: IPeriodTimeValues;
  currentDate: Date;
  periodType: TimeScale;

  init(): IPeriodTimeValues {
    const valueOfDays: IPeriodTimeValues = {};

    for (
      let startMoment = this.getStartMoment();
      startMoment <= this.getEndMoment();
      startMoment.add(1, 'millisecond')
    ) {
      const start = startMoment.valueOf();
      const end = this.getEndOfPeriodType(startMoment);
      startMoment = moment(end);
      const middle = Math.floor((start + end) / 2);
      const name = this.getName(middle, start, end, startMoment);
      const superName = this.getSuperName(startMoment);
      const { nextTitle, prevTitle } = messages.getChangeRangeName(
        this.periodType
      );
      valueOfDays[name] = {
        start,
        middle,
        end,
        name,
        superName,
        nextTitle,
        prevTitle,
      };
    }
    return valueOfDays;
  }
  abstract getName(
    middle: number,
    start: number,
    end: number,
    startMoment: moment.Moment
  ): string;
  abstract getEndMoment(): moment.Moment;
  abstract getSuperName(startMoment: moment.Moment): string;
  abstract getStartMoment(): moment.Moment;
  abstract getEndOfPeriodType(startMoment: moment.Moment): number;
  abstract getStartOfSinglePeriod(): moment.Moment;
  abstract getEndOfSinglePeriod(): moment.Moment;
}

class Day extends DataPeriod {
  constructor() {
    super();
    this.periodType = 'day';
  }
  getStartMoment() {
    return isPersianCalendar(this.lang)
      ? moment(this.currentDate).startOf('jMonth')
      : moment(this.currentDate).locale('en').startOf('month');
  }
  getEndMoment() {
    return isPersianCalendar(this.lang)
      ? moment(this.currentDate).endOf('jMonth')
      : moment(this.currentDate).locale('en').endOf('month');
  }
  getEndOfPeriodType(startMoment: moment.Moment) {
    return startMoment.endOf('day').valueOf();
  }
  getName(middle: number) {
    return isPersianCalendar(this.lang)
      ? moment(middle).format('jD')
      : moment(middle).locale('en').format('D');
  }
  getSuperName(startMoment: moment.Moment) {
    return isPersianCalendar(this.lang)
      ? startMoment.format('jMMMM jYYYY')
      : startMoment.locale('en').format('MMMM YYYY');
  }
  getStartOfSinglePeriod() {
    return isPersianCalendar(this.lang)
      ? moment(this.currentDate).startOf('day')
      : moment(this.currentDate).locale('en').startOf('day');
  }
  getEndOfSinglePeriod() {
    return isPersianCalendar(this.lang)
      ? moment(this.currentDate).endOf('day')
      : moment(this.currentDate).locale('en').endOf('day');
  }
}

class Week extends DataPeriod {
  constructor() {
    super();
    this.periodType = 'week';
  }
  getStartMoment() {
    return isPersianCalendar(this.lang)
      ? moment(this.currentDate).startOf('jMonth').startOf('week')
      : moment(this.currentDate).locale('en').startOf('month').startOf('week');
  }
  getEndMoment() {
    return isPersianCalendar(this.lang)
      ? moment(this.currentDate).endOf('jMonth').endOf('week')
      : moment(this.currentDate).locale('en').endOf('month').endOf('week');
  }
  getEndOfPeriodType(startMoment: moment.Moment) {
    return isPersianCalendar(this.lang)
      ? startMoment.endOf('week').valueOf()
      : startMoment.locale('en').endOf('week').valueOf();
  }
  getName(_middle: number, start: number, end: number) {
    const startOfWeek = isPersianCalendar(this.lang)
      ? moment(start).format('jD jMMMM')
      : moment(start).locale('en').format('D MMMM');
    const endOfWeek = isPersianCalendar(this.lang)
      ? moment(end).format('jD jMMMM')
      : moment(end).locale('en').format('D MMMM');
    return messages.getWeek(startOfWeek, endOfWeek);
  }
  getSuperName(startMoment: moment.Moment) {
    return messages.weeksOf(
      isPersianCalendar(this.lang)
        ? startMoment.format('jMMMM jYYYY')
        : startMoment.locale('en').format('MMMM YYYY')
    );
  }
  getStartOfSinglePeriod() {
    return isPersianCalendar(this.lang)
      ? moment(this.currentDate).startOf('week')
      : moment(this.currentDate).locale('en').startOf('week');
  }
  getEndOfSinglePeriod() {
    return isPersianCalendar(this.lang)
      ? moment(this.currentDate).endOf('week')
      : moment(this.currentDate).locale('en').endOf('week');
  }
}

class Month extends DataPeriod {
  constructor() {
    super();
    this.periodType = 'month';
  }
  getStartMoment() {
    return isPersianCalendar(this.lang)
      ? moment(this.currentDate).startOf('jYear')
      : moment(this.currentDate).locale('en').startOf('year');
  }
  getEndMoment() {
    return isPersianCalendar(this.lang)
      ? moment(this.currentDate).endOf('jYear')
      : moment(this.currentDate).locale('en').endOf('year');
  }
  getEndOfPeriodType(startMoment: moment.Moment) {
    return isPersianCalendar(this.lang)
      ? startMoment.endOf('jMonth').valueOf()
      : startMoment.locale('en').endOf('month').valueOf();
  }
  getName(middle: number) {
    return isPersianCalendar(this.lang)
      ? moment(middle).format('jMMMM')
      : moment(middle).locale('en').format('MMMM');
  }
  getSuperName(startMoment: moment.Moment) {
    return isPersianCalendar(this.lang)
      ? startMoment.format('jYYYY')
      : startMoment.locale('en').format('YYYY');
  }
  getStartOfSinglePeriod() {
    return isPersianCalendar(this.lang)
      ? moment(this.currentDate).startOf('jMonth')
      : moment(this.currentDate).locale('en').startOf('month');
  }
  getEndOfSinglePeriod() {
    return isPersianCalendar(this.lang)
      ? moment(this.currentDate).endOf('jMonth')
      : moment(this.currentDate).locale('en').endOf('month');
  }
}

class Season extends DataPeriod {
  constructor() {
    super();
    this.periodType = 'season';
  }
  getStartMoment() {
    return isPersianCalendar(this.lang)
      ? moment(this.currentDate).startOf('jYear')
      : moment(this.currentDate).locale('en').startOf('year');
  }
  getEndMoment() {
    return isPersianCalendar(this.lang)
      ? moment(this.currentDate).endOf('jYear')
      : moment(this.currentDate).locale('en').endOf('year');
  }
  getEndOfPeriodType(startMoment: moment.Moment) {
    return endOfDate(
      isPersianCalendar(this.lang)
        ? startMoment.toDate()
        : startMoment.locale('en').toDate(),
      'season',
      true,
      isPersianCalendar(this.lang) ? 'fa' : 'en'
    );
  }
  getName(
    _middle: number,
    _start: number,
    _end: number,
    startMoment: moment.Moment
  ) {
    // Todo : for english date we need to work with date as well as its month
    const month = englishNumber(
      isPersianCalendar(this.lang)
        ? startMoment.format('jM')
        : startMoment.locale('en').format('M')
    );
    return isPersianCalendar(this.lang)
      ? messages.getSeason(Number(month))
      : messages.getEnSeason(Number(month));
  }
  getSuperName(startMoment: moment.Moment) {
    return isPersianCalendar(this.lang)
      ? startMoment.format('jYYYY')
      : startMoment.locale('en').format('YYYY');
  }
  getStartOfSinglePeriod() {
    return isPersianCalendar(this.lang)
      ? moment(startOfDate(moment(this.currentDate).toDate(), 'season', true))
      : moment(
          startOfDate(
            moment(this.currentDate).locale('en').toDate(),
            'season',
            true,
            'en'
          )
        ).locale('en');
  }
  getEndOfSinglePeriod() {
    return isPersianCalendar(this.lang)
      ? moment(endOfDate(moment(this.currentDate).toDate(), 'season', true))
      : moment(
          endOfDate(
            moment(this.currentDate).locale('en').toDate(),
            'season',
            true,
            'en'
          )
        ).locale('en');
  }
}

class TwoSeason extends DataPeriod {
  constructor() {
    super();
    this.periodType = 'twoSeason';
  }
  getStartMoment() {
    return isPersianCalendar(this.lang)
      ? moment(this.currentDate).startOf('jYear')
      : moment(this.currentDate).locale('en').startOf('year');
  }
  getEndMoment() {
    return isPersianCalendar(this.lang)
      ? moment(this.currentDate).endOf('jYear')
      : moment(this.currentDate).locale('en').endOf('year');
  }
  getEndOfPeriodType(startMoment: moment.Moment) {
    return endOfDate(
      isPersianCalendar(this.lang)
        ? startMoment.toDate()
        : startMoment.locale('en').toDate(),
      'twoSeason',
      true,
      isPersianCalendar(this.lang) ? 'fa' : 'en'
    );
  }
  getName(middle: number) {
    const monthYear = englishNumber(
      isPersianCalendar(this.lang)
        ? moment(middle).format('jM')
        : moment(middle).locale('en').format('M')
    );
    return isPersianCalendar(this.lang)
      ? messages.getTwoSeasonName(monthYear)
      : messages.getTwoEnSeasonName(monthYear);
  }
  getSuperName(startMoment: moment.Moment) {
    return isPersianCalendar(this.lang)
      ? startMoment.format('jYYYY')
      : startMoment.locale('en').format('YYYY');
  }
  getStartOfSinglePeriod() {
    return isPersianCalendar(this.lang)
      ? moment(
          startOfDate(moment(this.currentDate).toDate(), 'twoSeason', true)
        )
      : moment(
          startOfDate(
            moment(this.currentDate).locale('en').toDate(),
            'twoSeason',
            true,
            'en'
          )
        ).locale('en');
  }
  getEndOfSinglePeriod() {
    return isPersianCalendar(this.lang)
      ? moment(endOfDate(moment(this.currentDate).toDate(), 'twoSeason', true))
      : moment(
          endOfDate(
            moment(this.currentDate).locale('en').toDate(),
            'twoSeason',
            true
          )
        ).locale('en');
  }
}

const isPersianCalendar = (local: string) => {
  return local === 'fa_IR@calendar=persian';
};

class Year extends DataPeriod {
  constructor() {
    super();
    this.periodType = 'year';
  }
  getStartMoment() {
    return isPersianCalendar(this.lang)
      ? moment(this.currentDate).startOf('jYear').subtract(5, 'jYear')
      : moment(this.currentDate)
          .locale('en')
          .startOf('year')
          .subtract(5, 'year');
  }
  getEndMoment() {
    return isPersianCalendar(this.lang)
      ? moment(this.currentDate).endOf('jYear')
      : moment(this.currentDate).locale('en').endOf('year');
  }
  getEndOfPeriodType(startMoment: moment.Moment) {
    return isPersianCalendar(this.lang)
      ? startMoment.endOf('jYear').valueOf()
      : startMoment.locale('en').endOf('year').valueOf();
  }
  getName(middle: number) {
    return isPersianCalendar(this.lang)
      ? moment(middle).format('jYYYY')
      : moment(middle).locale('en').format('YYYY');
  }
  getSuperName(_startMoment: moment.Moment) {
    return i18n.t('annual', { ns: 'survey' });
  }
  getStartOfSinglePeriod() {
    return isPersianCalendar(this.lang)
      ? moment(this.currentDate).startOf('jYear')
      : moment(this.currentDate).locale('en').startOf('year');
  }
  getEndOfSinglePeriod() {
    return isPersianCalendar(this.lang)
      ? moment(this.currentDate).endOf('jYear')
      : moment(this.currentDate).locale('en').endOf('year');
  }
}

const periods = {
  day: new Day(),
  week: new Week(),
  month: new Month(),
  season: new Season(),
  twoSeason: new TwoSeason(),
  year: new Year(),
};

export const getPeriodValues = (
  periodType: TimeScale,
  currentDate: Date,
  lang: 'fa_IR@calendar=persian' | 'en' = 'fa_IR@calendar=persian'
) => {
  const setPeriod = (selected: TimeScale) => periods[selected];

  const period: IDatePeriod = setPeriod(periodType);
  period.currentDate = currentDate;
  period.lang = lang;
  period.values = period.init();
  return period.values;
};

export const getEffectiveName = (
  periodType: TimeScale,
  selectedDate: number,
  local: string
): string => {
  const selectedMoment =
    local === 'en' ? moment(selectedDate).locale('en') : moment(selectedDate);
  const dayFormat = local === 'en' ? 'D MMMM YYYY' : 'jD jMMMM jYYYY';
  const weekFormat = local === 'en' ? 'D MMMM YYYY' : 'jD jMMMM jYYYY';
  const monthFormat = local === 'en' ? 'MMMM YYYY' : 'jMMMM jYYYY';
  const seasonMonthFormat = local === 'en' ? 'M' : 'jM';
  const seasonYearFormat = local === 'en' ? 'YYYY' : 'jYYYY';
  const twoSeasonFormat = local === 'en' ? 'M-YYYY' : 'jM-jYYYY';
  const yearFormat = local === 'en' ? 'YYYY' : 'jYYYY';
  const item: { [key: string]: string } = {
    day: selectedMoment.format(dayFormat),
    week: messages.getEndOfWeek(
      selectedMoment.endOf('week').format(weekFormat)
    ),
    month: selectedMoment.format(monthFormat),
    season: messages.getSuperSeasonName(
      Number(englishNumber(selectedMoment.format(seasonMonthFormat))),
      selectedMoment.format(seasonYearFormat)
    ),
    twoSeason: messages.getTwoSeasonName(
      englishNumber(selectedMoment.format(twoSeasonFormat))
    ),
    year: selectedMoment.format(yearFormat),
  };
  return item[periodType] && convertDigitToProperFormat(item[periodType]);
};

export const getClassObjectBaseOnPeriodType = (
  periodType: TimeScale,
  currentDate: number
) => {
  const setPeriod = (selected: TimeScale) => periods[selected];

  const period: DataPeriod = setPeriod(periodType) as DataPeriod;
  period.currentDate = new Date(currentDate);
  return {
    start: period.getStartOfSinglePeriod().valueOf(),
    end: period.getEndOfSinglePeriod().valueOf(),
  };
};
